import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

const reasons = [
  {
    title: "CBSE-Aligned Academic Excellence",
    content:
      "Krishna Public School strictly follows the CBSE (New Delhi) curriculum and the latest National Curriculum Framework (NCF), ensuring strong academic foundations, conceptual clarity, and future readiness.",
  },
  {
    title: "World-Class Infrastructure",
    content:
      "Spread over a lush green 2.5-acre campus, the school offers world-class infrastructure with spacious classrooms of approximately 600 sq. ft., providing a comfortable and engaging learning environment.",
  },
  {
    title: "Smart & Digital Learning",
    content:
      "Smart classrooms, projector-based teaching, computer-aided learning, and a LAN-enabled computer lab help students adapt to modern digital education.",
  },
  {
    title: "Experienced & Dedicated Faculty",
    content:
      "Our qualified and committed teachers focus on mentoring, individual attention, and continuous academic improvement for every student.",
  },
  {
    title: "Holistic Development Focus",
    content:
      "Equal importance is given to academics, sports, co-curricular activities, personality development, and emotional well-being.",
  },
  {
    title: "Sports & Co-Curricular Excellence",
    content:
      "The school offers a wide range of sports including basketball, volleyball, badminton, table tennis, tennis, and athletics, along with activities such as debates, quizzes, music, drama, art & craft, and educational excursions.",
  },
  {
    title: "Safe & Disciplined Campus",
    content:
      "A secure, disciplined, and value-driven environment ensures student safety while building confidence and responsibility.",
  },
];

export default function AboutUs() {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });
  }, []);

  return (
    <>
      {/* HERO SECTION */}
      <section className="bg-gradient-to-r from-red-600 to-[#ff4e1c] py-24 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-normal tracking-wide"
            data-aos="fade-down"
          >
            About{" "}
            <span className="scholarVarsity">Krishna Public School</span>
          </h1>

          <p
            className="mt-4 text-green-100 max-w-3xl mx-auto text-base md:text-lg"
            data-aos="fade-up"
            data-aos-delay="150"
          >
            Krishna Public School is a CBSE-affiliated institution committed to
            academic excellence, character building, and holistic development in
            a safe, future-ready learning environment.
          </p>
        </div>
      </section>

      {/* ABOUT OUR SCHOOL */}
      <section className="max-w-6xl mx-auto px-3 py-20">
        <div className="text-center mb-14">
          <h2 className="text-4xl font-bold text-gray-900" data-aos="fade-up">
            About Our School
          </h2>

          <div
            className="w-28 h-1 bg-[#ff4e1c] mx-auto mt-4 rounded-full"
            data-aos="zoom-in"
            data-aos-delay="150"
          />
        </div>

        <div
          className="text-gray-700 leading-relaxed text-lg max-w-6xl mx-auto text-center space-y-6"
          data-aos="fade-up"
          data-aos-delay="250"
        >
          <p>
            <span className="scholarVarsity">Krishna Public School</span> is a
            CBSE-affiliated institution (Affiliation No. 2132602, School Code
            70808) dedicated to nurturing young minds through quality education,
            strong values, and modern teaching practices. Our mission is to
            create confident, responsible, and future-ready citizens.
          </p>

          <p>
            Spread across a lush green campus of <strong>2.5 acres</strong>,
            Krishna Public School offers world-class infrastructure designed to
            support academic and co-curricular excellence. Our spacious
            classrooms, each measuring approximately{" "}
            <strong>600 sq. ft.</strong>, provide a comfortable and engaging
            learning environment for every student.
          </p>

          <p>
            Teaching at Krishna Public School is based on the{" "}
            <strong>CBSE (New Delhi) curriculum</strong> and the latest National
            Curriculum Framework (NCF), with English as the medium of
            instruction. Our approach emphasizes conceptual clarity, critical
            thinking, creativity, and practical application of knowledge.
          </p>

          <p>
            The school is equipped with smart classrooms, projector-based
            learning, a well-stocked library, modern science laboratories, and a
            large computer lab with LAN networking to support computer-aided
            learning and digital literacy.
          </p>

          <p>
            Beyond academics, Krishna Public School believes in holistic
            development. Students actively participate in sports such as
            basketball, volleyball, badminton, table tennis, tennis, and
            athletics, along with co-curricular activities including debates,
            quizzes, music, drama, photography, art & craft, and educational
            excursions.
          </p>

          <p>
            To support emotional and personal growth, the school also conducts
            yoga sessions, career counseling, and personality development
            programs. At Krishna Public School, every child’s potential is
            recognized, nurtured, and guided toward lifelong success.
          </p>
        </div>
      </section>

      {/* WHY PARENTS CHOOSE US */}
      <section
        className="relative bg-gray-50 py-24 overflow-hidden"
        id="facilities"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.15),transparent_60%)]" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900" data-aos="fade-up">
              Why Parents Choose Us
            </h2>

            <p
              className="mt-4 text-gray-600"
              data-aos="fade-up"
              data-aos-delay="150"
            >
              More than education — a strong foundation for life
            </p>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            {reasons.map((item, index) => (
              <div
                key={index}
                onClick={() => setOpen(open === index ? null : index)}
                className={`cursor-pointer rounded-2xl border border-white/60 
                  bg-white/70 backdrop-blur-xl p-6 transition-all duration-500
                  hover:-translate-y-1 hover:shadow-xl
                  ${open === index ? "ring-2 ring-green-400 shadow-2xl" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {item.title}
                  </h3>

                  <span className="text-green-600 text-2xl font-bold">
                    {open === index ? "–" : "+"}
                  </span>
                </div>

                <div
                  className={`transition-all duration-500 overflow-hidden ${
                    open === index
                      ? "max-h-[500px] mt-4 opacity-100"
                      : "max-h-0 opacity-100"
                  }`}
                >
                  <p className="text-gray-600 leading-relaxed">
                    {item.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="relative bg-gray-50 py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.15),transparent_60%)]" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900" data-aos="fade-up">
              Give Your Child the Education They Deserve
            </h2>

            <p
              className="mt-4 text-gray-600"
              data-aos="fade-up"
              data-aos-delay="150"
            >
              Because the right foundation today shapes a confident tomorrow.
            </p>
          </div>

          <p
            className="text-gray-700 leading-relaxed text-lg max-w-4xl mx-auto text-center"
            data-aos="fade-up"
            data-aos-delay="250"
          >
            In today’s fast-changing world, education must go beyond textbooks.
            At{" "}
            <span className="scholarVarsity">Krishna Public School</span>, we
            combine strong academics, modern infrastructure, experienced
            educators, and value-based learning to prepare students for lifelong
            success. Give your child an education that builds confidence,
            competence, and character.
          </p>
        </div>
      </section>
    </>
  );
}
